import { DeleteOutlined } from '@ant-design/icons'
import {
  ResponseTypeEnum,
  TemplateNodeSchema,
  TemplateNodeTypeEnum,
} from '@ulysses-inc/harami_api_client'
import { Button, Modal } from 'antd'
import { FC } from 'react'
import { useSelector } from 'react-redux'
import { useAddTemplateSpecificMultipleChoices } from 'src/features/templateEdit/templateSpecificMultipleChoices/hooks/useAddTemplateSpecificMultipleChoices'
import { useDeleteTemplateSpecificMultipleChoices } from 'src/features/templateEdit/templateSpecificMultipleChoices/hooks/useDeleteTemplateSpecificMultipleChoices'
import { useUpdateTemplateSpecificMultipleChoices } from 'src/features/templateEdit/templateSpecificMultipleChoices/hooks/useUpdateTemplateSpecificMultipleChoices'
import { useSourceTemplateIdParam } from 'src/features/templateEdit/useSourceTemplateIdParam'
import { RootState } from 'src/state/store'
import DeleteConfirmDialog from 'src/views/components/dialogs/DeleteConfirmDialog'
import { useIsMultipleChoiceGroupInUse } from '../../hooks/useIsMultipleChoiceGroupInUse'
import {
  ButtonGroup,
  ButtonGroupRow,
  FooterButton,
} from '../../style/MultipleChoiceDrawer.styled'
import AlertModalContent from '../AlertModalContent/AlertModalContent'
import {
  createMultipleChoiceButtonsCommon,
  MultipleChoiceButtonsRowProps,
} from './multipleChoiceButtonsCommon'

/**
 * 選択肢群を使用している質問のタイトル一覧を取得
 */
const getQuestionsUsingMultipleChoice = (
  groupId: number,
  templateNodes: { [key: number]: TemplateNodeSchema },
): string[] => {
  return Object.values(templateNodes)
    .filter(
      ({ type, question }) =>
        type === TemplateNodeTypeEnum.Question &&
        question?.responseType === ResponseTypeEnum.MULTIPLE_CHOICE &&
        question?.responseMultipleChoices?.some(group => group?.id === groupId),
    )
    .map(node => node.question?.name || '（質問名なし）')
}

/**
 * ひな形限定の選択肢用のボタン行コンポーネント
 * - TanStack Query mutations を使用
 * - ひな形限定だがrelatedTemplatesが必要(未保存の状態ではなく保存済みデータを基準に削除可否を判断するため)
 * - 現在のひな形内での使用状況のみチェック
 */
const TemplateSpecificMultipleChoiceButtonsRow: FC<
  MultipleChoiceButtonsRowProps
> = ({
  onClose,
  multipleChoicesState: [multipleChoices, setMultipleChoices],
  multipleChoiceGroupForUpdate,
  relatedTemplates,
  isFetchingRelatedTemplates,
  isErrorRelatedTemplates,
}) => {
  const isInUse = useIsMultipleChoiceGroupInUse(multipleChoiceGroupForUpdate)
  const templateId = useSourceTemplateIdParam()
  const templateName = useSelector(
    (state: RootState) => state.templatesState.editTemplate?.name || '',
  )
  const templateNodes = useSelector(
    (state: RootState) => state.templatesState.templatePages.templateNodes,
  )

  const {
    addTemplateSpecificMultipleChoices,
    isPendingAddTemplateSpecificMultipleChoices,
  } = useAddTemplateSpecificMultipleChoices()
  const {
    updateTemplateSpecificMultipleChoices,
    isPendingUpdateTemplateSpecificMultipleChoices,
  } = useUpdateTemplateSpecificMultipleChoices()
  const {
    deleteTemplateSpecificMultipleChoices,
    isPendingDeleteTemplateSpecificMultipleChoices,
  } = useDeleteTemplateSpecificMultipleChoices()

  const { handleCloseDrawer, getFilteredMultipleChoices } =
    createMultipleChoiceButtonsCommon(
      multipleChoices,
      setMultipleChoices,
      onClose,
    )

  const isNew = multipleChoiceGroupForUpdate === undefined

  const handleSave = (close: () => void) => {
    const filteredMultipleChoices = getFilteredMultipleChoices()

    if (filteredMultipleChoices.length > 0) {
      const onSuccess = () => {
        handleCloseDrawer()
      }

      if (multipleChoiceGroupForUpdate) {
        updateTemplateSpecificMultipleChoices(
          {
            id: multipleChoiceGroupForUpdate.id ?? 0,
            multipleChoices: filteredMultipleChoices,
          },
          onSuccess,
        )
      } else {
        addTemplateSpecificMultipleChoices(filteredMultipleChoices, onSuccess)
      }

      // ひな形限定の選択肢は、処理に成功した場合のみドロワーを閉じるようにするため、ここではモーダルを閉じるだけにする
      close()
      return
    }

    close()
    handleCloseDrawer()
  }

  const confirmModal = () => {
    // 編集時に使用中の質問をチェック
    if (multipleChoiceGroupForUpdate && isInUse) {
      const usingQuestions = getQuestionsUsingMultipleChoice(
        multipleChoiceGroupForUpdate.id ?? 0,
        templateNodes,
      )

      Modal.confirm({
        onOk: handleSave,
        title: '選択肢を保存してよろしいですか？',
        content: (
          <div>
            <p>この選択肢は以下の質問で使用中です。</p>
            <ul>
              {usingQuestions.map((questionTitle, index) => (
                <li key={index}>{questionTitle}</li>
              ))}
            </ul>
          </div>
        ),
        okText: '保存する',
        cancelText: 'キャンセル',
      })
      return
    }

    // 新規作成または未使用の選択肢の場合
    Modal.confirm({
      onOk: handleSave,
      title: '選択肢を保存してよろしいですか？',
      okText: '保存する',
      cancelText: 'キャンセル',
    })
  }

  const confirmDeleteModal = () => {
    const deletingMultipleChoiceGroupId = multipleChoiceGroupForUpdate?.id
    if (!deletingMultipleChoiceGroupId) return

    // サーバーから取得した使用状況をチェック（編集中の状態ではなく保存済みデータを基準）
    if (relatedTemplates?.length || isInUse) {
      Modal.error({
        title: 'このひな形で使用されているため削除できません。',
        content: (
          <AlertModalContent
            relatedTemplates={relatedTemplates ?? null}
            templateId={templateId}
            templateName={templateName}
            isInUse={isInUse}
          />
        ),
      })
      return
    }

    DeleteConfirmDialog({
      title: '選択肢を削除してよろしいですか？',
      onOk: () => {
        deleteTemplateSpecificMultipleChoices(
          deletingMultipleChoiceGroupId,
          () => {
            handleCloseDrawer()
          },
        )
      },
    })
  }

  return (
    <ButtonGroupRow>
      <ButtonGroup>
        {!isNew && (
          <Button
            danger
            disabled={
              isFetchingRelatedTemplates ||
              isErrorRelatedTemplates ||
              isPendingUpdateTemplateSpecificMultipleChoices
            }
            loading={isPendingDeleteTemplateSpecificMultipleChoices}
            type="link"
            icon={<DeleteOutlined />}
            onClick={confirmDeleteModal}
          >
            選択肢を削除
          </Button>
        )}
      </ButtonGroup>
      <ButtonGroup>
        <FooterButton
          type="link"
          onClick={() => {
            onClose()
            setMultipleChoices({})
          }}
        >
          キャンセル
        </FooterButton>
        <FooterButton
          disabled={
            isFetchingRelatedTemplates ||
            isErrorRelatedTemplates ||
            isPendingDeleteTemplateSpecificMultipleChoices
          }
          loading={
            isPendingAddTemplateSpecificMultipleChoices ||
            isPendingUpdateTemplateSpecificMultipleChoices
          }
          type="primary"
          onClick={confirmModal}
        >
          保存
        </FooterButton>
      </ButtonGroup>
    </ButtonGroupRow>
  )
}

export default TemplateSpecificMultipleChoiceButtonsRow
